import type { ReactNode } from "react"

import { tv } from "@/lib/utils/tv"

const emptyState = tv({
  base: "flex flex-col items-center gap-200 px-600 text-center",
  variants: {
    size: {
      sm: "py-800",
      lg: "py-800 @3xl:py-1200",
    },
  },
  defaultVariants: { size: "sm" },
})

const emptyTitle = tv({
  base: "font-display text-fg text-balance",
  variants: {
    size: {
      sm: "text-heading-sm font-bold",
      lg: "text-heading-md @3xl:text-heading-lg font-semibold tracking-tight",
    },
  },
  defaultVariants: { size: "sm" },
})

const emptyBody = tv({
  base: "text-fg-subtle text-balance",
  variants: {
    size: {
      sm: "text-body-sm max-w-measure-sm",
      lg: "text-body-lg max-w-measure-sm",
    },
  },
  defaultVariants: { size: "sm" },
})

interface EmptyStateProps {
  icon?: ReactNode
  title: ReactNode
  children?: ReactNode
  action?: ReactNode
  // lg gives the surface a display-scale statement; use it where the empty
  // state is the whole page, not a panel inside one.
  size?: "sm" | "lg"
  className?: string
}

export const EmptyState = ({ icon, title, children, action, size = "sm", className }: EmptyStateProps) => (
  <div className={emptyState({ size, className })}>
    {icon && <span className="bg-surface-newgray text-fg-subtle mb-100 flex size-15 items-center justify-center rounded-full">{icon}</span>}
    <h3 className={emptyTitle({ size })}>{title}</h3>
    {children && <p className={emptyBody({ size })}>{children}</p>}
    {action && <div className={size === "lg" ? "mt-400" : "mt-300"}>{action}</div>}
  </div>
)
